import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import { createPortal } from 'react-dom';
import MediaMtxWebRtcPlayer from './MediaMtxWebRtcPlayer.jsx';

const EMPTY_OBJECT = Object.freeze({});
const DEFAULT_LIVE_TRACKER = Object.freeze({ trackingThreshold: 45, liveTolerance: 15 });

const resolveContainedMediaRect = (mediaEl) => {
    if (!mediaEl || typeof mediaEl.getBoundingClientRect !== 'function') {
        return null;
    }

    const rect = mediaEl.getBoundingClientRect();
    const boxWidth = rect.width;
    const boxHeight = rect.height;

    if (!boxWidth || !boxHeight) {
        return null;
    }

    const intrinsicWidth = Number(mediaEl.videoWidth || mediaEl.clientWidth || boxWidth);
    const intrinsicHeight = Number(mediaEl.videoHeight || mediaEl.clientHeight || boxHeight);

    if (!intrinsicWidth || !intrinsicHeight) {
        return rect;
    }

    const mediaAspect = intrinsicWidth / intrinsicHeight;
    const boxAspect = boxWidth / boxHeight;

    let visibleWidth = boxWidth;
    let visibleHeight = boxHeight;
    let offsetX = 0;
    let offsetY = 0;

    if (boxAspect > mediaAspect) {
        visibleWidth = boxHeight * mediaAspect;
        offsetX = (boxWidth - visibleWidth) / 2;
    } else if (boxAspect < mediaAspect) {
        visibleHeight = boxWidth / mediaAspect;
        offsetY = (boxHeight - visibleHeight) / 2;
    }

    return {
        left: rect.left + offsetX,
        top: rect.top + offsetY,
        width: visibleWidth,
        height: visibleHeight,
        right: rect.left + offsetX + visibleWidth,
        bottom: rect.top + offsetY + visibleHeight,
    };
};

const resolveAutoPlayFlag = (value) => {
    if (value === true) {
        return true;
    }
    if (value === false || value === null || typeof value === 'undefined') {
        return false;
    }
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['false', 'off', '0', 'none', 'manual', 'no'].includes(normalized)) {
            return false;
        }
        return true;
    }
    return Boolean(value);
};

const HLS_MIME_TYPE = 'application/x-mpegURL';

const normalizeSources = (sources, fallbackUrl, fallbackType) => {
    const normalized = Array.isArray(sources)
        ? sources.filter((entry) => entry && entry.src).map((entry) => {
              const type = entry.type || fallbackType || HLS_MIME_TYPE;
              const shouldUseCredentials = typeof entry.withCredentials === 'boolean'
                  ? entry.withCredentials
                  : /mpegurl/i.test(type);

              return {
                  ...entry,
                  src: entry.src,
                  type,
                  withCredentials: shouldUseCredentials,
              };
          })
        : [];

    if (!normalized.length && fallbackUrl) {
        const type = fallbackType || HLS_MIME_TYPE;
        normalized.push({
            src: fallbackUrl,
            type,
            withCredentials: /mpegurl/i.test(type),
        });
    }

    return normalized;
};

const setBufferGoal = (player, bufferGoalSeconds) => {
    const seconds = Number(bufferGoalSeconds);
    if (!player || !seconds || Number.isNaN(seconds) || seconds <= 0) {
        return;
    }

    try {
        if (player.vhs) {
            player.vhs.bufferGoal = seconds;
        }
        const tech = player.tech({ IWillNotUseThisInPlugins: true });
        if (tech?.hls) {
            tech.hls.goalBufferLength = seconds;
        }
        if (tech?.hlsSourceHandler?.setOptions) {
            tech.hlsSourceHandler.setOptions({ bufferGoal: seconds, goalBufferLength: seconds });
        }
    } catch (error) {
        console.warn('[LiveVideoPlayer] Unable to set buffer goal', error);
    }
};

const fetchWithAuth = (url, authToken, debugLabel) => {
    if (!url) {
        return Promise.resolve();
    }

    const headers = authToken
        ? {
              Authorization: `Bearer ${authToken}`,
          }
        : undefined;

    return fetch(url, { method: 'GET', headers }).catch((error) => {
        console.warn(`[LiveVideoPlayer] ${debugLabel ?? 'Request'} failed`, error);
    });
};

const LiveVideoPlayer = ({
    playbackProtocol = 'hls',
    playerId,
    className = 'video-js vjs-default-skin',
    wrapperClassName = '',
    sources = [],
    sourceUrl,
    sourceType = HLS_MIME_TYPE,
    controls = true,
    autoplay = false,
    muted = false,
    loop = false,
    preload = 'auto',
    fluid = true,
    height,
    width,
    live = false,
    poster,
    playsInline = true,
    html5,
    liveTracker,
    bufferGoalSeconds = 5,
    allowReload = false,
    showReloadButton = true,
    reloadButtonClassName = 'btn btn-sm btn-rose mt-3',
    reloadButtonText = 'Reload video',
    reloadOnError = true,
    reloadLimit = 6,
    reloadDelayMs = 2000,
    onlineStatusUrl,
    onlineStatusImmediate = true,
    onlineStatusInterval = 0,
    onlineStatusInitialDelay = 0,
    keepAliveUrl,
    keepAliveImmediate = false,
    keepAliveInterval = 0,
    authToken,
    debug = false,
    skipFailedSegments = true,
    skipFailedSegmentOffsetSeconds = 0.5,
    skipFailedSegmentMaxAttempts = 3,
    skipFailedSegmentRecoveryMs = 1500,
    gridColumns = 0,
    gridRows = 0,
    gridDebug = false,
    gridOverlayClassName = 'live-video-player__grid-overlay',
    gridOverlayColor = 'rgba(255, 0, 0, 0.25)',
    onGridCellClick,
    overlayActions = [],
    overlayVisible = false,
    overlayClassName = '',
    footerActions = [],
    onReady,
    onError,
}) => {
    if (playbackProtocol === 'webrtc') {
        return (
            <MediaMtxWebRtcPlayer
                playerId={playerId}
                className={className}
                wrapperClassName={wrapperClassName}
                sourceUrl={sourceUrl}
                controls={controls}
                autoplay={autoplay}
                muted={muted}
                loop={loop}
                preload={preload}
                fluid={fluid}
                height={height}
                width={width}
                live={live}
                playsInline={playsInline}
                allowReload={allowReload}
                showReloadButton={showReloadButton}
                reloadButtonClassName={reloadButtonClassName}
                reloadButtonText={reloadButtonText}
                reloadOnError={reloadOnError}
                reloadLimit={reloadLimit}
                reloadDelayMs={reloadDelayMs}
                onlineStatusUrl={onlineStatusUrl}
                onlineStatusImmediate={onlineStatusImmediate}
                onlineStatusInterval={onlineStatusInterval}
                onlineStatusInitialDelay={onlineStatusInitialDelay}
                keepAliveUrl={keepAliveUrl}
                keepAliveImmediate={keepAliveImmediate}
                keepAliveInterval={keepAliveInterval}
                authToken={authToken}
                debug={debug}
                gridColumns={gridColumns}
                gridRows={gridRows}
                gridDebug={gridDebug}
                gridOverlayClassName={gridOverlayClassName}
                gridOverlayColor={gridOverlayColor}
                onGridCellClick={onGridCellClick}
                overlayActions={overlayActions}
                overlayVisible={overlayVisible}
                overlayClassName={overlayClassName}
                footerActions={footerActions}
                onReady={onReady}
                onError={onError}
            />
        );
    }

    const internalId = useId();
    const resolvedId = useMemo(
        () => playerId || `live-video-${internalId.replace(/[:]/g, '')}`,
        [playerId, internalId]
    );
    const rootRef = useRef(null);
    const [isBrowserFullscreen, setIsBrowserFullscreen] = useState(false);

    const rootClassName = useMemo(
        () => ['live-video-player', wrapperClassName, isBrowserFullscreen ? 'live-video-player--fullscreen' : '']
            .filter(Boolean)
            .join(' '),
        [isBrowserFullscreen, wrapperClassName]
    );

    const videoNodeRef = useRef(null);
    const playerRef = useRef(null);
    const reloadCountRef = useRef(0);
    const requestTimersRef = useRef([]);
    const gridOverlayRef = useRef(null);
    const skippedSegmentsRef = useRef({});
    const overlayPortalRef = useRef(null);
    const [overlayPortalEl, setOverlayPortalEl] = useState(null);
    const [playerState, setPlayerState] = useState({ paused: false, lastUpdate: null });

    const resolvedHtml5 = useMemo(() => html5 ?? EMPTY_OBJECT, [html5]);
    const resolvedLiveTracker = useMemo(() => {
        if (!live) {
            return DEFAULT_LIVE_TRACKER;
        }
        if (!liveTracker) {
            return DEFAULT_LIVE_TRACKER;
        }
        return { ...DEFAULT_LIVE_TRACKER, ...liveTracker };
    }, [live, liveTracker]);

    const autoPlayAttr = useMemo(() => resolveAutoPlayFlag(autoplay), [autoplay]);
    const shouldAttemptAutoStart = useMemo(() => autoPlayAttr || muted || live, [autoPlayAttr, live, muted]);
    const resolvedPreload = useMemo(() => {
        if (typeof preload === 'boolean') {
            return preload ? 'auto' : 'none';
        }
        return preload ?? 'auto';
    }, [preload]);

    const gridEnabled = useMemo(
        () => gridColumns > 0 && gridRows > 0,
        [gridColumns, gridRows]
    );

    const resolvedSources = useMemo(
        () => normalizeSources(sources, sourceUrl, sourceType),
        [sources, sourceUrl, sourceType]
    );

    const basePlayerOptions = useMemo(() => {
        const options = {
            controls,
            autoplay: typeof autoplay === 'string' ? autoplay : autoPlayAttr,
            loop,
            muted,
            preload: resolvedPreload,
            fluid,
            poster,
        };

        if (typeof height !== 'undefined' && height !== null) {
            options.height = height;
        }
        if (typeof width !== 'undefined' && width !== null) {
            options.width = width;
        }

        const html5Base = {
            nativeAudioTracks: false,
            nativeVideoTracks: false,
        };

        if (live) {
            options.liveui = true;
            options.html5 = {
                ...html5Base,
                vhs: {
                    overrideNative: true,
                    allowSeeksWithinUnsafeLiveWindow: true,
                    lowLatencyMode: false,
                    experimentalLLHLS: false,
                    withCredentials: true,
                    handleManifestRedirects: true,
                },
                ...resolvedHtml5,
            };
            options.liveTracker = resolvedLiveTracker;
        } else {
            options.html5 = {
                ...html5Base,
                vhs: {
                    withCredentials: true,
                    handleManifestRedirects: true,
                },
                ...resolvedHtml5,
            };
        }

        return options;
    }, [autoPlayAttr, controls, fluid, height, live, loop, muted, poster, resolvedHtml5, resolvedLiveTracker, resolvedPreload, width]);

    const playerOptionsRef = useRef(basePlayerOptions);
    useEffect(() => {
        playerOptionsRef.current = basePlayerOptions;
    }, [basePlayerOptions]);

    const applySources = useCallback(
        (player) => {
            if (!player || !resolvedSources.length) {
                return;
            }

            skippedSegmentsRef.current = {};

            if (debug) {
                console.debug('[LiveVideoPlayer] Applying sources', resolvedSources);
            }

            const attemptPlayback = () => {
                const playPromise = player.play();
                if (playPromise?.catch) {
                    playPromise.catch((error) => {
                        if (debug) {
                            console.debug('[LiveVideoPlayer] Autoplay attempt was blocked', error);
                        }
                    });
                }
            };

            player.src(resolvedSources);

            player.ready(() => {
                setBufferGoal(player, bufferGoalSeconds);
                if (shouldAttemptAutoStart) {
                    attemptPlayback();
                }
                onReady?.(player);
            });

            player.one('loadedmetadata', () => {
                setBufferGoal(player, bufferGoalSeconds);
                if (shouldAttemptAutoStart) {
                    attemptPlayback();
                }
            });
        },
        [bufferGoalSeconds, debug, onReady, resolvedSources, shouldAttemptAutoStart]
    );

    const latestApplySourcesRef = useRef(applySources);
    useEffect(() => {
        latestApplySourcesRef.current = applySources;
    }, [applySources]);

    const scheduleRequest = useCallback((callback, delay, repeating = false) => {
        if (typeof callback !== 'function' || delay === null || delay === undefined) {
            return;
        }
        const timerId = repeating ? setInterval(callback, delay) : setTimeout(callback, delay);
        requestTimersRef.current.push({ id: timerId, repeating });
    }, []);

    const clearScheduledRequests = useCallback(() => {
        requestTimersRef.current.forEach(({ id, repeating }) => {
            if (repeating) {
                clearInterval(id);
            } else {
                clearTimeout(id);
            }
        });
        requestTimersRef.current = [];
    }, []);

    const attemptSkipFailedSegment = useCallback(
        (player, error) => {
            if (!skipFailedSegments || !player || !error) {
                return null;
            }

            const errorCode = Number(error.code);
            if (![3, 4].includes(errorCode)) {
                return null;
            }

            const message = error.message || '';
            const segmentMatch = message.match(/segment\s*#?(\d+)/i);
            const segmentKey = segmentMatch?.[1] ?? 'unknown';

            const attempts = (skippedSegmentsRef.current[segmentKey] || 0) + 1;
            skippedSegmentsRef.current[segmentKey] = attempts;

            if (attempts > skipFailedSegmentMaxAttempts) {
                if (debug) {
                    console.debug('[LiveVideoPlayer] Skip attempts exceeded for segment', segmentKey);
                }
                return null;
            }

            const seekable = typeof player.seekable === 'function' ? player.seekable() : null;
            const seekableLength = seekable?.length ?? 0;
            if (!seekableLength) {
                if (debug) {
                    console.debug('[LiveVideoPlayer] Unable to skip segment; no seekable range available');
                }
                return null;
            }

            let targetTime = null;

            try {
                const rangeEnd = seekable.end(seekableLength - 1);
                const rangeStart = seekable.start(0);
                if (Number.isFinite(rangeEnd) && Number.isFinite(rangeStart)) {
                    const offset = Math.max(skipFailedSegmentOffsetSeconds, 0);
                    targetTime = Math.max(rangeStart, rangeEnd - offset);
                }
            } catch (rangeError) {
                if (debug) {
                    console.debug('[LiveVideoPlayer] Failed to read seekable range', rangeError);
                }
                return null;
            }

            if (!Number.isFinite(targetTime)) {
                return null;
            }

            if (debug) {
                console.debug('[LiveVideoPlayer] Skipping errored segment', segmentKey, 'attempt', attempts, 'targetTime', targetTime);
            }

            try {
                player.error(null);
                if (live && typeof player.liveTracker?.seekToLiveEdge === 'function') {
                    player.liveTracker.seekToLiveEdge();
                    const currentTime = player.currentTime();
                    if (!Number.isFinite(currentTime) || currentTime < targetTime - 1) {
                        player.currentTime(targetTime);
                    }
                } else {
                    player.currentTime(targetTime);
                }
            } catch (seekError) {
                if (debug) {
                    console.warn('[LiveVideoPlayer] Failed to seek past errored segment', seekError);
                }
                return null;
            }

            const tech = typeof player.tech === 'function' ? player.tech({ IWillNotUseThisInPlugins: true }) : null;
            if (tech?.setCurrentTime && Number.isFinite(targetTime)) {
                try {
                    tech.setCurrentTime(targetTime);
                } catch (techSeekError) {
                    if (debug) {
                        console.debug('[LiveVideoPlayer] Tech seek failed', techSeekError);
                    }
                }
            }

            reloadCountRef.current = 0;

            const playPromise = player.play();
            if (playPromise?.catch) {
                playPromise.catch(() => {});
            }

            return {
                segmentKey,
                attempts,
                targetTime,
            };
        },
        [debug, live, skipFailedSegmentMaxAttempts, skipFailedSegmentOffsetSeconds, skipFailedSegments]
    );

    useEffect(() => {
        const updateFullscreenState = () => {
            const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
            const rootEl = rootRef.current;
            setIsBrowserFullscreen(Boolean(rootEl && fullscreenElement && rootEl.contains(fullscreenElement)));
        };

        document.addEventListener('fullscreenchange', updateFullscreenState);
        document.addEventListener('webkitfullscreenchange', updateFullscreenState);
        updateFullscreenState();

        return () => {
            document.removeEventListener('fullscreenchange', updateFullscreenState);
            document.removeEventListener('webkitfullscreenchange', updateFullscreenState);
        };
    }, []);

    useEffect(() => {
        const videoElement = videoNodeRef.current;
        if (!videoElement) {
            return undefined;
        }

        const player = videojs(videoElement, playerOptionsRef.current, () => {
            if (debug) {
                console.debug('[LiveVideoPlayer] Player initialized', resolvedId);
            }
            latestApplySourcesRef.current(player);
        });

        playerRef.current = player;

        return () => {
            clearScheduledRequests();
            playerRef.current = null;
            player.dispose();
        };
    }, [clearScheduledRequests, debug, resolvedId]);

    useEffect(() => {
        const player = playerRef.current;
        if (!player) {
            return;
        }

        player.controls(controls);
        player.loop(loop);
        player.muted(muted);
        player.autoplay(typeof autoplay === 'string' ? autoplay : autoPlayAttr);
        if (resolvedPreload) {
            player.preload(resolvedPreload);
        }
    }, [autoPlayAttr, autoplay, controls, loop, muted, resolvedPreload]);

    useEffect(() => {
        const player = playerRef.current;
        if (!player) {
            return undefined;
        }

        const updatePausedState = () => {
            const paused = typeof player.paused === 'function' ? player.paused() : false;
            setPlayerState({ paused, lastUpdate: Date.now() });
        };

        updatePausedState();

        const events = ['play', 'playing', 'pause', 'ended', 'waiting'];
        events.forEach((eventName) => player.on(eventName, updatePausedState));

        return () => {
            events.forEach((eventName) => player.off(eventName, updatePausedState));
        };
    }, [resolvedId]);

    useEffect(() => {
        const player = playerRef.current;
        if (!player || !resolvedSources.length) {
            return;
        }

        const currentSources = typeof player.currentSources === 'function' ? player.currentSources() : [];
        const normalizedCurrent = currentSources.map(({ src, type }) => ({
            src,
            type: type || HLS_MIME_TYPE,
        }));

        const hasSameSources =
            normalizedCurrent.length === resolvedSources.length &&
            normalizedCurrent.every((entry, index) => entry.src === resolvedSources[index].src);

        if (!hasSameSources) {
            if (debug) {
                console.debug('[LiveVideoPlayer] Detected source change', resolvedSources);
            }
            applySources(player);
            reloadCountRef.current = 0;
        }
    }, [applySources, debug, resolvedSources]);

    useEffect(() => {
        const player = playerRef.current;
        if (!player) {
            return;
        }

        setBufferGoal(player, bufferGoalSeconds);
    }, [bufferGoalSeconds]);

    useEffect(() => {
        const player = playerRef.current;
        if (!player) {
            return;
        }

        const handleError = () => {
            const error = player.error();
            if (debug && error) {
                console.warn('[LiveVideoPlayer] Player error', error);
            }
            onError?.(error);

            const skipResult = attemptSkipFailedSegment(player, error);
            if (skipResult) {
                if (skipFailedSegmentRecoveryMs > 0) {
                    scheduleRequest(() => {
                        const activePlayer = playerRef.current;
                        if (!activePlayer || activePlayer.error()) {
                            return;
                        }

                        const readyState = typeof activePlayer.readyState === 'function' ? activePlayer.readyState() : 0;
                        const isPaused = typeof activePlayer.paused === 'function' ? activePlayer.paused() : false;
                        const seekable = typeof activePlayer.seekable === 'function' ? activePlayer.seekable() : null;
                        let distanceToEdge = Number.POSITIVE_INFINITY;

                        try {
                            if (live && seekable?.length) {
                                const end = seekable.end(seekable.length - 1);
                                if (Number.isFinite(end)) {
                                    distanceToEdge = end - activePlayer.currentTime();
                                }
                            }
                        } catch (rangeError) {
                            if (debug) {
                                console.debug('[LiveVideoPlayer] Recovery check failed to compute seekable gap', rangeError);
                            }
                        }

                        const recovered = readyState >= 3 && !isPaused && (!live || distanceToEdge <= skipFailedSegmentOffsetSeconds + 1);

                        if (!recovered) {
                            if (debug) {
                                console.debug('[LiveVideoPlayer] Recovery fallback after skipped segment', skipResult.segmentKey);
                            }
                            applySources(activePlayer);
                        }
                    }, skipFailedSegmentRecoveryMs, false);
                }
                return;
            }

            if (!reloadOnError || reloadCountRef.current >= reloadLimit) {
                return;
            }

            reloadCountRef.current += 1;
            const attemptReload = () => {
                if (!playerRef.current) {
                    return;
                }
                applySources(playerRef.current);
            };

            if (reloadDelayMs > 0) {
                setTimeout(attemptReload, reloadDelayMs);
            } else {
                attemptReload();
            }
        };

        player.on('error', handleError);
        return () => {
            player.off('error', handleError);
        };
    }, [applySources, attemptSkipFailedSegment, debug, live, onError, reloadDelayMs, reloadLimit, reloadOnError, scheduleRequest, skipFailedSegmentOffsetSeconds, skipFailedSegmentRecoveryMs]);

    useEffect(() => {
        clearScheduledRequests();

        if (onlineStatusUrl) {
            const triggerOnlineStatus = () => fetchWithAuth(onlineStatusUrl, authToken, 'Online status');
            if (onlineStatusImmediate) {
                triggerOnlineStatus();
            }
            if (onlineStatusInitialDelay > 0) {
                scheduleRequest(triggerOnlineStatus, onlineStatusInitialDelay, false);
            }
            if (onlineStatusInterval > 0) {
                scheduleRequest(triggerOnlineStatus, onlineStatusInterval, true);
            }
        }

        if (keepAliveUrl) {
            const triggerKeepAlive = () => fetchWithAuth(keepAliveUrl, authToken, 'Keep alive');
            if (keepAliveImmediate) {
                triggerKeepAlive();
            }
            if (keepAliveInterval > 0) {
                scheduleRequest(triggerKeepAlive, keepAliveInterval, true);
            }
        }

        return clearScheduledRequests;
    }, [authToken, clearScheduledRequests, keepAliveImmediate, keepAliveInterval, keepAliveUrl, onlineStatusImmediate, onlineStatusInitialDelay, onlineStatusInterval, onlineStatusUrl, scheduleRequest]);

    useEffect(() => {
        const player = playerRef.current;
        if (!player) {
            return undefined;
        }

        let cleanup = () => {};

        const setupGridOverlay = () => {
            cleanup();

            if (!gridEnabled) {
                return;
            }

            const rootEl = typeof player.el === 'function' ? player.el() : player.el;
            const techEl = rootEl?.querySelector('.vjs-tech');

            if (!rootEl || !techEl) {
                return;
            }

            const overlayEl = document.createElement('div');
            const classNames = new Set(['live-video-player__grid-overlay']);
            if (gridOverlayClassName) {
                gridOverlayClassName
                    .split(' ')
                    .map((name) => name.trim())
                    .filter(Boolean)
                    .forEach((name) => classNames.add(name));
            }

            overlayEl.className = Array.from(classNames).join(' ');
            overlayEl.style.position = 'absolute';
            overlayEl.style.pointerEvents = 'auto';
            overlayEl.style.zIndex = '4';

            if (gridDebug && gridColumns > 0 && gridRows > 0) {
                const columnSize = `${(100 / gridColumns).toFixed(4)}%`;
                const rowSize = `${(100 / gridRows).toFixed(4)}%`;
                overlayEl.style.backgroundImage = `linear-gradient(to right, ${gridOverlayColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridOverlayColor} 1px, transparent 1px)`;
                overlayEl.style.backgroundSize = `${columnSize} ${rowSize}`;
                overlayEl.style.backgroundRepeat = 'repeat';
            } else {
                overlayEl.style.backgroundImage = '';
                overlayEl.style.backgroundSize = '';
            }

            gridOverlayRef.current = overlayEl;

            if (getComputedStyle(rootEl).position === 'static') {
                rootEl.dataset.gridOriginalPosition = 'static';
                rootEl.style.position = 'relative';
            }

            rootEl.classList.add('live-video-player--grid-enabled');
            rootEl.appendChild(overlayEl);

            const updateOverlayBounds = () => {
                const rootRect = rootEl.getBoundingClientRect();
                const mediaRect = resolveContainedMediaRect(techEl) || techEl.getBoundingClientRect();

                overlayEl.style.left = `${Math.max(0, mediaRect.left - rootRect.left)}px`;
                overlayEl.style.top = `${Math.max(0, mediaRect.top - rootRect.top)}px`;
                overlayEl.style.width = `${Math.max(0, mediaRect.width)}px`;
                overlayEl.style.height = `${Math.max(0, mediaRect.height)}px`;
            };

            updateOverlayBounds();

            const handleClick = (event) => {
                if (!(event.target === overlayEl || overlayEl.contains(event.target))) {
                    return;
                }

                const rect = resolveContainedMediaRect(techEl) || techEl.getBoundingClientRect();
                if (!rect.width || !rect.height) {
                    return;
                }

                const relativeX = (event.clientX - rect.left) / rect.width;
                const relativeY = (event.clientY - rect.top) / rect.height;

                if (typeof event.stopImmediatePropagation === 'function') {
                    event.stopImmediatePropagation();
                }
                event.stopPropagation();
                event.preventDefault();
                if (relativeX < 0 || relativeX > 1 || relativeY < 0 || relativeY > 1) {
                    return;
                }

                const column = Math.min(gridColumns, Math.max(1, Math.ceil(relativeX * gridColumns)));
                const row = Math.min(gridRows, Math.max(1, Math.ceil(relativeY * gridRows)));

                if (onGridCellClick) {
                    onGridCellClick(row, column, event);
                }

                const playerInstance = playerRef.current;
                if (playerInstance && typeof playerInstance.play === 'function' && playerInstance.paused()) {
                    playerInstance.play().catch(() => {});
                }

                if (videoNodeRef.current) {
                    const detail = { row, column, nativeEvent: event };
                    const customEvent = typeof window !== 'undefined' && typeof window.CustomEvent === 'function'
                        ? new window.CustomEvent('livevideoplayer:grid-cell-click', { detail })
                        : null;
                    if (customEvent) {
                        videoNodeRef.current.dispatchEvent(customEvent);
                    }
                }
            };

            rootEl.addEventListener('click', handleClick, true);
            player.on('loadedmetadata', updateOverlayBounds);
            player.on('resize', updateOverlayBounds);
            window.addEventListener('resize', updateOverlayBounds);
            document.addEventListener('fullscreenchange', updateOverlayBounds);

            cleanup = () => {
                rootEl.removeEventListener('click', handleClick, true);
                player.off('loadedmetadata', updateOverlayBounds);
                player.off('resize', updateOverlayBounds);
                window.removeEventListener('resize', updateOverlayBounds);
                document.removeEventListener('fullscreenchange', updateOverlayBounds);
                if (gridOverlayRef.current === overlayEl) {
                    gridOverlayRef.current.remove();
                    gridOverlayRef.current = null;
                } else {
                    overlayEl.remove();
                }
                rootEl.classList.remove('live-video-player--grid-enabled');
                if (rootEl.dataset.gridOriginalPosition === 'static') {
                    rootEl.style.position = '';
                    delete rootEl.dataset.gridOriginalPosition;
                }
            };
        };

        const readyHandler = () => {
            setupGridOverlay();
        };

        if (player.isReady_) {
            setupGridOverlay();
        } else {
            player.one('ready', readyHandler);
        }

        return () => {
            player.off('ready', readyHandler);
            cleanup();
        };
    }, [gridColumns, gridDebug, gridEnabled, gridOverlayClassName, gridOverlayColor, gridRows, onGridCellClick]);

    const handleReload = useCallback(() => {
        const player = playerRef.current;
        if (!player) {
            return;
        }

        if (debug) {
            console.debug('[LiveVideoPlayer] Manual reload requested');
        }

        reloadCountRef.current = 0;
        applySources(player);
    }, [applySources, debug]);

    const normalizedOverlayActions = useMemo(
        () => (Array.isArray(overlayActions) ? overlayActions.filter(Boolean) : []),
        [overlayActions]
    );
    const normalizedFooterActions = useMemo(
        () => (Array.isArray(footerActions) ? footerActions.filter(Boolean) : []),
        [footerActions]
    );
    const overlayActionCount = normalizedOverlayActions.length;

    const actionsContainerClassName = useMemo(() => {
        const classes = ['live-video-player__actions'];
        if (overlayVisible) {
            classes.push('live-video-player__actions--active');
        }
        if (overlayClassName) {
            classes.push(overlayClassName);
        }
        return classes.join(' ');
    }, [overlayClassName, overlayVisible]);

    const isPaused = Boolean(playerState.paused);
    const showPausedOverlay = isPaused;

    useEffect(() => {
        const player = playerRef.current;
        if (!player) {
            if (overlayPortalRef.current) {
                overlayPortalRef.current.remove();
                overlayPortalRef.current = null;
            }
            setOverlayPortalEl(null);
            return undefined;
        }

        let cleanup = () => {};

        const setupOverlayActions = () => {
            cleanup();

            const rootEl = typeof player.el === 'function' ? player.el() : player.el;
            if (!rootEl) {
                return;
            }

            if (overlayActionCount === 0) {
                if (overlayPortalRef.current) {
                    overlayPortalRef.current.remove();
                    overlayPortalRef.current = null;
                }
                rootEl.classList.remove('live-video-player--actions-enabled');
                if (rootEl.dataset.actionsOriginalPosition === 'static') {
                    rootEl.style.position = '';
                    delete rootEl.dataset.actionsOriginalPosition;
                }
                setOverlayPortalEl(null);
                cleanup = () => {};
                return;
            }

            if (getComputedStyle(rootEl).position === 'static') {
                rootEl.dataset.actionsOriginalPosition = 'static';
                rootEl.style.position = 'relative';
            }

            let container = overlayPortalRef.current;
            if (!container) {
                container = document.createElement('div');
                container.classList.add('live-video-player__actions-root');
                container.style.position = 'absolute';
                container.style.top = '0';
                container.style.right = '0';
                container.style.zIndex = '6';
                rootEl.appendChild(container);
                overlayPortalRef.current = container;
            } else if (!rootEl.contains(container)) {
                rootEl.appendChild(container);
            }

            rootEl.classList.add('live-video-player--actions-enabled');
            setOverlayPortalEl(container);

            cleanup = () => {
                if (overlayPortalRef.current === container) {
                    overlayPortalRef.current.remove();
                    overlayPortalRef.current = null;
                } else if (container.parentNode) {
                    container.parentNode.removeChild(container);
                }
                rootEl.classList.remove('live-video-player--actions-enabled');
                if (rootEl.dataset.actionsOriginalPosition === 'static') {
                    rootEl.style.position = '';
                    delete rootEl.dataset.actionsOriginalPosition;
                }
                setOverlayPortalEl((current) => (current === container ? null : current));
            };
        };

        if (player.isReady_) {
            setupOverlayActions();
        } else {
            player.one('ready', setupOverlayActions);
        }

        return () => {
            player.off('ready', setupOverlayActions);
            cleanup();
        };
    }, [overlayActionCount]);

    return (
        <div ref={rootRef} className={rootClassName} style={{ position: 'relative' }}>
            {overlayPortalEl && overlayActionCount > 0
                ? createPortal(
                      <div className={actionsContainerClassName}>
                          {normalizedOverlayActions.map((action, index) => {
                              const {
                                  key,
                                  label,
                                  icon,
                                  hint,
                                  buttonClassName,
                                  onClick: actionHandler,
                                  disabled,
                                  title,
                              } = action;

                              const resolvedKey = key ?? `overlay-action-${index}`;
                              const resolvedButtonClass = buttonClassName || 'btn btn-sm btn-outline-secondary';

                              return (
                                  <React.Fragment key={resolvedKey}>
                                      <button
                                          type="button"
                                          className={resolvedButtonClass}
                                          onClick={(event) => {
                                              if (typeof actionHandler === 'function') {
                                                  actionHandler(event);
                                              }
                                          }}
                                          disabled={Boolean(disabled)}
                                          title={title || label}
                                      >
                                          {icon ? <i className="material-icons mr-1">{icon}</i> : null}
                                          {label ?? ''}
                                      </button>
                                      {hint ? <span className="live-video-player__actions-hint">{hint}</span> : null}
                                  </React.Fragment>
                              );
                          })}
                      </div>,
                      overlayPortalEl
                  )
                : null}
            {showPausedOverlay ? (
                <div
                    className="live-video-player__paused-indicator"
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '80px',
                        height: '80px',
                        borderRadius: '999px',
                        background: 'rgba(0, 0, 0, 0.45)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        zIndex: 7,
                    }}
                >
                    <span className="material-icons" style={{ fontSize: '48px', lineHeight: 1 }}>
                        play_arrow
                    </span>
                </div>
            ) : null}
            <div data-vjs-player>
                <video
                    id={resolvedId}
                    ref={videoNodeRef}
                    className={className}
                    playsInline={playsInline}
                    muted={muted}
                    loop={loop}
                    controls={controls}
                    autoPlay={autoPlayAttr}
                    preload={resolvedPreload}
                    crossOrigin="anonymous"
                />
            </div>
            {!isBrowserFullscreen && (allowReload || normalizedFooterActions.length > 0) ? (
                <div className="live-video-player__footer d-flex flex-wrap align-items-center" style={{ gap: '0.5rem' }}>
                    {allowReload && showReloadButton ? (
                        <button type="button" className={reloadButtonClassName} onClick={handleReload}>
                            {reloadButtonText}
                        </button>
                    ) : null}
                    {normalizedFooterActions.map((action, index) => {
                        const resolvedKey = action.key ?? `footer-action-${index}`;
                        const resolvedButtonClass = action.buttonClassName || 'btn btn-sm btn-outline-secondary mt-3';

                        return (
                            <button
                                key={resolvedKey}
                                type="button"
                                className={resolvedButtonClass}
                                onClick={(event) => {
                                    if (typeof action.onClick === 'function') {
                                        action.onClick(event);
                                    }
                                }}
                                disabled={Boolean(action.disabled)}
                                title={action.title || action.label}
                            >
                                {action.icon ? <i className="material-icons mr-1">{action.icon}</i> : null}
                                {action.label ?? ''}
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
};

LiveVideoPlayer.propTypes = {
    playbackProtocol: PropTypes.oneOf(['hls', 'webrtc']),
    playerId: PropTypes.string,
    className: PropTypes.string,
    wrapperClassName: PropTypes.string,
    sources: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
            type: PropTypes.string,
        })
    ),
    sourceUrl: PropTypes.string,
    sourceType: PropTypes.string,
    controls: PropTypes.bool,
    autoplay: PropTypes.bool,
    muted: PropTypes.bool,
    loop: PropTypes.bool,
    preload: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    fluid: PropTypes.bool,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    live: PropTypes.bool,
    poster: PropTypes.string,
    playsInline: PropTypes.bool,
    html5: PropTypes.object,
    liveTracker: PropTypes.object,
    bufferGoalSeconds: PropTypes.number,
    allowReload: PropTypes.bool,
    showReloadButton: PropTypes.bool,
    reloadButtonClassName: PropTypes.string,
    reloadButtonText: PropTypes.string,
    reloadOnError: PropTypes.bool,
    reloadLimit: PropTypes.number,
    reloadDelayMs: PropTypes.number,
    onlineStatusUrl: PropTypes.string,
    onlineStatusImmediate: PropTypes.bool,
    onlineStatusInterval: PropTypes.number,
    onlineStatusInitialDelay: PropTypes.number,
    keepAliveUrl: PropTypes.string,
    keepAliveImmediate: PropTypes.bool,
    keepAliveInterval: PropTypes.number,
    authToken: PropTypes.string,
    debug: PropTypes.bool,
    skipFailedSegments: PropTypes.bool,
    skipFailedSegmentOffsetSeconds: PropTypes.number,
    skipFailedSegmentMaxAttempts: PropTypes.number,
    skipFailedSegmentRecoveryMs: PropTypes.number,
    gridColumns: PropTypes.number,
    gridRows: PropTypes.number,
    gridDebug: PropTypes.bool,
    gridOverlayClassName: PropTypes.string,
    gridOverlayColor: PropTypes.string,
    onGridCellClick: PropTypes.func,
    overlayActions: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
            icon: PropTypes.string,
            hint: PropTypes.string,
            buttonClassName: PropTypes.string,
            onClick: PropTypes.func,
            disabled: PropTypes.bool,
            title: PropTypes.string,
        })
    ),
    overlayVisible: PropTypes.bool,
    overlayClassName: PropTypes.string,
    footerActions: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
            icon: PropTypes.string,
            buttonClassName: PropTypes.string,
            onClick: PropTypes.func,
            disabled: PropTypes.bool,
            title: PropTypes.string,
        })
    ),
    onReady: PropTypes.func,
    onError: PropTypes.func,
};

export default LiveVideoPlayer;
